import { create } from 'zustand';
import { Pipeline, PipelineNode, PipelineEdge } from '../types/pipeline';
import { useRunLogStore } from './runLogStore';
import { getTenantId } from './authStore';
import { uuid } from '../lib/uuid';
import { followRun, toProgress, isRunInFlight, type RunProgress, type RunSnapshot } from './pipelineRunFollow';

const PIPELINE_API = import.meta.env.VITE_PIPELINE_SERVICE_URL || 'http://localhost:8002';

// ─── Schedule Interface ──────────────────────────────────────────────────────

export interface PipelineSchedule {
  id: string;
  pipeline_id: string;
  tenant_id: string;
  name: string;
  cron_expression: string;
  enabled: boolean;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Keys whose VALUES are user-defined dicts (arbitrary keys like Excel column
// names with spaces / slashes / capitals) — recursing into them would
// camelToSnake the user's data and break downstream lookups. e.g. a user-typed
// MAP mapping `"PR Number": "pr_number"` would become `"_p_r _number": "pr_number"`,
// the source key no longer matches anything in the records, and zero renames
// happen. The backend's dual-key reads (`cfg.get("connectorId") or cfg.get("connector_id")`)
// handle the camelCase fallback at the API layer, so leaving these objects
// untransformed is safe. snakeToCamel MUST honor the same set on read —
// otherwise a fetch camelizes the user's keys and the next save persists them
// corrupted, since camelToSnake skips opaque values and never converts back.
const OPAQUE_KEYS = new Set([
  'config', 'mappings', 'casts', 'headers', 'queryParams', 'query_params',
  'transforms', 'rules', 'enrichments', 'fieldMappings', 'field_mappings',
  'context', 'attributes', 'metadata', 'meta',
]);

function snakeToCamel(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const opaque = OPAQUE_KEYS.has(k) || OPAQUE_KEYS.has(camel);
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[camel] = opaque ? v : snakeToCamel(v as Record<string, unknown>);
    } else if (Array.isArray(v)) {
      out[camel] = v.map((item) =>
        item && typeof item === 'object' && !opaque
          ? snakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else {
      out[camel] = v;
    }
  }
  return out;
}

function camelToSnake(obj: Record<string, unknown>, parentKey: string = ''): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const snake = k.replace(/([A-Z])/g, (c) => `_${c.toLowerCase()}`);
    const opaque = OPAQUE_KEYS.has(k) || OPAQUE_KEYS.has(snake);
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[snake] = opaque ? v : camelToSnake(v as Record<string, unknown>, snake);
    } else if (Array.isArray(v)) {
      out[snake] = v.map((item) =>
        item && typeof item === 'object' && !opaque
          ? camelToSnake(item as Record<string, unknown>, snake)
          : item
      );
    } else {
      out[snake] = v;
    }
  }
  return out;
}

/** The run a pipeline is executing right now, if the server has one. */
async function findInFlightRun(pipelineId: string): Promise<string | undefined> {
  try {
    const r = await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/runs`, { headers: { 'x-tenant-id': getTenantId() } });
    if (!r.ok) return undefined;
    const runs = (await r.json()) as { id: string; status: string }[];
    return runs.find((x) => isRunInFlight(x.status))?.id;
  } catch {
    return undefined;
  }
}

// ─── Store ──────────────────────────────────────────────────────────────────

interface PipelineStoreState {
  pipelines: Pipeline[];
  selectedPipelineId: string | null;
  loading: boolean;
  error: string | null;
  schedules: PipelineSchedule[];
  /** Live state of the run each pipeline is executing right now. */
  runProgress: Record<string, RunProgress | undefined>;

  fetchPipelines: () => Promise<void>;
  selectPipeline: (id: string) => void;
  addPipeline: (pipeline: Pipeline, schedule?: { name: string; cron_expression: string; enabled?: boolean }) => Promise<Pipeline>;
  updatePipeline: (id: string, updates: Partial<Pipeline>) => Promise<void>;
  updatePipelineNodes: (id: string, nodes: PipelineNode[], edges: PipelineEdge[]) => Promise<void>;
  removePipeline: (id: string) => Promise<void>;
  runPipeline: (id: string) => Promise<{ run_id: string; status: string }>;
  setPipelines: (pipelines: Pipeline[]) => void;
  fetchSchedules: (pipelineId: string) => Promise<void>;
  createSchedule: (pipelineId: string, data: { name: string; cron_expression: string; enabled?: boolean }) => Promise<void>;
  updateSchedule: (pipelineId: string, scheduleId: string, data: Partial<{ name: string; cron_expression: string; enabled: boolean }>) => Promise<void>;
  deleteSchedule: (pipelineId: string, scheduleId: string) => Promise<void>;
  runScheduleNow: (pipelineId: string, scheduleId: string) => Promise<void>;
}

export const usePipelineStore = create<PipelineStoreState>((set, get) => ({
  pipelines: [],
  selectedPipelineId: null,
  loading: false,
  error: null,
  schedules: [],
  runProgress: {},

  fetchPipelines: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${PIPELINE_API}/pipelines`, { headers: { 'x-tenant-id': getTenantId() } });
      if (!res.ok) throw new Error(`Failed to fetch pipelines: ${res.status}`);
      const data = await res.json();
      const pipelines = data.map((item: Record<string, unknown>) => snakeToCamel(item) as unknown as Pipeline);
      set({ pipelines, loading: false });
    } catch (err) {
      set({ error: String(err), loading: false });
    }
  },

  selectPipeline: (id) => set({ selectedPipelineId: id }),

  addPipeline: async (pipeline: Pipeline, schedule) => {
    const body = camelToSnake({ ...pipeline, ...(schedule ? { schedule } : {}) } as unknown as Record<string, unknown>);
    const res = await fetch(`${PIPELINE_API}/pipelines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Failed to create pipeline: ${res.status}`);
    const data = await res.json();
    const created = snakeToCamel(data) as unknown as Pipeline;
    set((state) => ({ pipelines: [...state.pipelines, created] }));
    return created;
  },

  updatePipeline: async (id: string, updates: Partial<Pipeline>) => {
    const existing = get().pipelines.find((p) => p.id === id);
    if (!existing) return;
    const merged = { ...existing, ...updates };
    const body = camelToSnake(merged as unknown as Record<string, unknown>);
    const res = await fetch(`${PIPELINE_API}/pipelines/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Failed to update pipeline: ${res.status}`);
    const data = await res.json();
    const updated = snakeToCamel(data) as unknown as Pipeline;
    set((state) => ({
      pipelines: state.pipelines.map((p) => (p.id === id ? updated : p)),
    }));
  },

  updatePipelineNodes: async (id: string, nodes: PipelineNode[], edges: PipelineEdge[]) => {
    const existing = get().pipelines.find((p) => p.id === id);
    if (!existing) return;
    const merged = { ...existing, nodes, edges };
    const body = camelToSnake(merged as unknown as Record<string, unknown>);
    const res = await fetch(`${PIPELINE_API}/pipelines/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Failed to update pipeline nodes: ${res.status}`);
    const data = await res.json();
    const updated = snakeToCamel(data) as unknown as Pipeline;
    set((state) => ({
      pipelines: state.pipelines.map((p) => (p.id === id ? updated : p)),
    }));
  },

  removePipeline: async (id: string) => {
    const res = await fetch(`${PIPELINE_API}/pipelines/${id}`, { method: 'DELETE', headers: { 'x-tenant-id': getTenantId() } });
    if (!res.ok && res.status !== 204) throw new Error(`Failed to delete pipeline: ${res.status}`);
    set((state) => ({ pipelines: state.pipelines.filter((p) => p.id !== id) }));
  },

  runPipeline: async (id: string) => {
    const pipeline = get().pipelines.find((p) => p.id === id);
    const startedAt = new Date().toISOString();

    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.id === id ? { ...p, status: 'RUNNING' as any } : p
      ),
    }));

    const setProgress = (p: RunProgress | undefined) =>
      set((state) => ({ runProgress: { ...state.runProgress, [id]: p } }));

    // Kick off the run
    let runId: string | undefined;
    let kickError: string | undefined;
    try {
      const res = await fetch(`${PIPELINE_API}/pipelines/${id}/run`, { method: 'POST', headers: { 'x-tenant-id': getTenantId() } });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // Already running (another tab, a schedule, a trigger). That is not a
        // failure of this click: follow the run that is in flight.
        runId = await findInFlightRun(id);
        if (!runId) throw new Error(data?.detail || `HTTP ${res.status}`);
      } else {
        if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
        runId = data.run_id;
      }
    } catch (err) {
      kickError = String(err);
    }

    if (kickError || !runId) {
      const status = 'FAILED';
      set((state) => ({
        pipelines: state.pipelines.map((p) => p.id === id ? { ...p, status: status as any } : p),
      }));
      useRunLogStore.getState().addLog({
        id: uuid(),
        pipeline_id: id,
        pipeline_name: pipeline?.name || id,
        status,
        rows_in: 0,
        rows_out: 0,
        error: kickError,
        node_audits: [],
        started_at: startedAt,
        finished_at: new Date().toISOString(),
        read: false,
      });
      return { run_id: '', status };
    }

    // Follow the run until the server says it is over. There is no client
    // timeout: a long run keeps showing as running, never as failed.
    const followedRun = runId;
    setProgress({ runId: followedRun, status: 'RUNNING', startedAt });
    const outcome = await followRun({
      fetchRun: async () => {
        const r = await fetch(`${PIPELINE_API}/pipelines/${id}/runs/${followedRun}/audit`, { headers: { 'x-tenant-id': getTenantId() } });
        return { status: r.status, body: r.ok ? ((await r.json()) as RunSnapshot) : undefined };
      },
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      onTick: (snap) => setProgress(toProgress(snap, followedRun, startedAt)),
    });
    setProgress(undefined);

    if (outcome.kind !== 'finished') {
      // The server forgot the run (deleted pipeline, purged runs). We know
      // nothing about how it ended, so we do not claim it failed: take the
      // pipeline's state from the server and say so.
      await get().fetchPipelines();
      return { run_id: followedRun, status: 'UNKNOWN' };
    }
    const audit = outcome.run as unknown as Record<string, unknown>;

    const status: 'COMPLETED' | 'FAILED' =
      (audit.status as string) === 'COMPLETED' ? 'COMPLETED' : 'FAILED';

    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.id === id ? { ...p, status: status as any } : p
      ),
    }));

    // node_audits is a dict keyed by node_id — flatten to array
    const rawAudits = audit.node_audits as Record<string, any> | null | undefined;
    const nodeAudits = rawAudits
      ? Object.values(rawAudits).map((a) => ({
          node_id: a.node_id,
          node_type: a.node_type,
          label: a.node_label,
          status: (a.error ? 'error' : 'ok') as 'ok' | 'error' | 'skipped',
          rows_in: a.rows_in ?? 0,
          rows_out: a.rows_out ?? 0,
          error: a.error,
          duration_ms: a.duration_ms,
          stats: a.stats,
          sample_out: a.sample_out,
        }))
      : [];

    useRunLogStore.getState().addLog({
      id: runId,
      pipeline_id: id,
      pipeline_name: pipeline?.name || id,
      status,
      rows_in: (audit.rows_in as number) ?? 0,
      rows_out: (audit.rows_out as number) ?? 0,
      error: audit.error_message as string | undefined,
      node_audits: nodeAudits,
      started_at: (audit.started_at as string) || startedAt,
      finished_at: (audit.finished_at as string) || new Date().toISOString(),
      read: false,
    });

    return { run_id: runId, status };
  },

  setPipelines: (pipelines) => set({ pipelines }),

  fetchSchedules: async (pipelineId) => {
    const r = await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/schedules`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    const fetched: PipelineSchedule[] = Array.isArray(data) ? data : [];
    // Merge: replace schedules for this pipeline, keep others
    set((s) => ({
      schedules: [
        ...s.schedules.filter((sc) => sc.pipeline_id !== pipelineId),
        ...fetched,
      ],
    }));
  },

  createSchedule: async (pipelineId, data) => {
    const r = await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/schedules`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) throw new Error(`Failed to create schedule: ${r.status}`);
    await get().fetchSchedules(pipelineId);
  },

  updateSchedule: async (pipelineId, scheduleId, data) => {
    const r = await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/schedules/${scheduleId}`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) throw new Error(`Failed to update schedule: ${r.status}`);
    await get().fetchSchedules(pipelineId);
  },

  deleteSchedule: async (pipelineId, scheduleId) => {
    await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': getTenantId() },
    });
    await get().fetchSchedules(pipelineId);
  },

  runScheduleNow: async (pipelineId, scheduleId) => {
    await fetch(`${PIPELINE_API}/pipelines/${pipelineId}/schedules/${scheduleId}/run-now`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId() },
    });
  },
}));
